import type { SchemaType } from '@based/schema'
import { SchemaToType } from './utils'

export const CONTENT_BLOCK_TYPE = {
  text: 'text',
  data: 'data',
  steps: 'steps',
  documents: 'documents',
  location: 'location',
  ctaGrid: 'ctaGrid',
  stories: 'stories',
  news: 'news',
  thread: 'thread',
  initiatives: 'initiatives',
  faqs: 'faqs',
  contactPanel: 'contactPanel',
} as const
export type ContentBlockType = keyof typeof CONTENT_BLOCK_TYPE

export const pageContentBlock = {
  uuid: { type: 'alias', format: 'UUID' },
  type: Object.values(CONTENT_BLOCK_TYPE),
  title: 'string',
  body: { type: 'text', format: 'html' },
  order: 'number',
  initiative: {
    ref: 'initiative',
    prop: 'contentBlocks',
    $position: ['overviewTab'],
    $positionIndex: 'number',
  },
  plot: {
    ref: 'plot',
    prop: 'contentBlocks',
    $position: [
      'overviewTabTop',
      'overviewTabBottom',
      'applicationTab',
      'documentsTab',
    ],
  },
  serviceProvider: { ref: 'serviceProvider', prop: 'contentBlocks' },
  businessProfile: { ref: 'businessProfile', prop: 'contentBlocks' },
  municipality: {
    ref: 'municipality',
    prop: 'contentBlocks',
    $position: ['overviewTab'],
    $positionIndex: 'number',
  },
  region: {
    ref: 'region',
    prop: 'contentBlocks',
    $position: ['overviewTab'],
    $positionIndex: 'number',
  },
  topicCommunity: {
    ref: 'topicCommunity',
    prop: 'contentBlocks',
    $position: ['overviewTab', 'contactTab'],
    $positionIndex: 'number',
  },
  truncationThreshold: 'uint8',
  listItems: {
    items: {
      ref: 'pageContentListItem',
      prop: 'contentBlock',
      $order: 'number',
    },
  },
  createdAt: { type: 'timestamp', on: 'create' },
  updatedAt: { type: 'timestamp', on: 'update' },
} satisfies SchemaType

export type PageContentBlock = Omit<
  SchemaToType<typeof pageContentBlock>,
  'type'
> & {
  id: number
  type: ContentBlockType
}

export function sortContentBlocksByOrder<T extends { order?: number }>(
  blocks: T[],
): T[] {
  return [...blocks].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
}

export const pageContentListItem = {
  uuid: { type: 'alias', format: 'UUID' },
  contentBlock: {
    ref: 'pageContentBlock',
    prop: 'listItems',
    $order: 'number',
  },
  type: ['data', 'steps', 'documents', 'cta', 'contact'],
  label: 'string',
  content: 'string',
  alert: 'string',
  file: { ref: 'file', prop: 'pageContentFiles' },
  image: { ref: 'file', prop: 'pageContentImages' },
  linkText: 'string',
  linkUrl: 'string',
  backgroundColor: 'string',
  size: ['large', 'medium', 'small'],
} satisfies SchemaType

export type PageContentListItem = SchemaToType<typeof pageContentListItem> & {
  id: number
}
